import { useState } from "react";
import { X } from "lucide-react";
import api from "../../../../../../utils/axiosInstance";
import { toast } from "../../../../../../components/Toast/toast";
import { btn } from "../../../../../../components/Button/buttonStyles";
import FormInput from "../../../../../../components/Form/FormInput";

const TABS = [
  { key: "upload", label: "Upload New File" },
  { key: "url", label: "Add URL" },
];

// Same frame as UpdateLinkedFilesModal, minus the project-files tab: outputs
// are produced by the job, so they are uploaded rather than linked.
const UpdateOutputFilesModal = ({ jobId, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState("upload");

  const [selectedFile, setSelectedFile] = useState(null);
  const [inputForJob, setInputForJob] = useState("");
  const [uploading, setUploading] = useState(false);

  const [fileName, setFileName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("job_id", jobId);
      formData.append("input_for_job", inputForJob);

      await api.post("/job-output-files", formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("File uploaded successfully");
      onSuccess();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.message || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  // TODO: wire to backend once an add-URL endpoint exists for output files.
  const handleAddUrl = () => {
    toast.error("Adding output files from a URL is coming soon.");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Update Job Output Files</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 px-6 py-3 text-sm font-medium ${
                activeTab === tab.key
                  ? "border-b-2 border-brand-600 text-brand-600"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content - fixed height so the modal doesn't jump when switching tabs */}
        <div className="p-6 overflow-y-auto h-[260px] max-h-[calc(90vh-200px)]">
          {activeTab === "upload" ? (
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-600">
                  Choose File <span className="text-danger-500">*</span>
                </label>
                <input
                  type="file"
                  onChange={(e) => e.target.files[0] && setSelectedFile(e.target.files[0])}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                />
                {selectedFile && (
                  <p className="mt-2 text-sm text-gray-600">
                    Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </p>
                )}
              </div>

              <div>
                <FormInput
                  label="Input for Job (Optional)"
                  name="input_for_job"
                  value={inputForJob}
                  onChange={(e) => setInputForJob(e.target.value)}
                  placeholder="Job ID or name this output will be input for"
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Specify if this output will be used as input for another job.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <FormInput
                label="File Name"
                name="output_file_name"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="e.g., output.pdf"
                required
              />
              <div>
                <FormInput
                  label="File URL"
                  name="output_file_url"
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com/file.pdf"
                  required
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Adding output files from a URL is not available yet.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button onClick={onClose} className={btn("secondary", "sm")}>
            Cancel
          </button>
          {activeTab === "upload" ? (
            <button
              onClick={handleUpload}
              disabled={uploading || !selectedFile}
              className={btn("primary", "sm")}
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
          ) : (
            <button
              onClick={handleAddUrl}
              disabled={!linkUrl.trim() || !fileName.trim()}
              className={btn("primary", "sm")}
            >
              Add
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateOutputFilesModal;
